import { useEffect, useRef, useState, type FormEvent } from "react";

import { messages as api, type DeviceMessage } from "../lib/api.js";
import { enterAction, needsMarking, readThrough, unreadCount } from "../lib/discussion.js";
import { relativeTime } from "../lib/freshness.js";
import { MAX_PHOTOS, preparePhoto, type PreparedPhoto } from "../lib/photo.js";
import { notifyUnreadChanged } from "../lib/unread-events.js";
import { useLiveData } from "../lib/use-live-data.js";
import { useNow } from "../lib/use-now.js";

/**
 * The conversation attached to one device, as a collapsible panel.
 *
 * Everyone who can read the device can read and post here, including the
 * organizations it has been shared with -- so every message carries the
 * organization its author was speaking for, and the note at the top says
 * plainly who else is in the room.
 *
 * Opening the panel is what marks the conversation read, and only while the
 * page is actually visible: polling pauses in a background tab, so a
 * conversation left open behind other windows does not quietly mark messages
 * as seen that nobody has looked at. Collapsed, it reads nothing and marks
 * nothing; it just carries the unread count.
 *
 * Polled rather than pushed. A websocket for a handful of operators leaving
 * each other notes about a solar controller would be a second transport to
 * operate, and this is the only part of the product that would need it.
 */

const REFRESH_MS = 20_000;

export type DiscussionMode = "column" | "stacked";

export function DeviceChat({
  deviceId,
  open,
  onToggle,
  mode,
}: {
  deviceId: string;
  open: boolean;
  onToggle: () => void;
  /** Beside the content on a wide screen, or above it on a narrow one. */
  mode: DiscussionMode;
}) {
  const now = useNow();
  const { data, error, reload } = useLiveData(() => api.list(deviceId), REFRESH_MS, [deviceId]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState<PreparedPhoto[]>([]);
  const [preparing, setPreparing] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const count = data?.messages.length ?? 0;
  const previousCount = useRef(0);

  /**
   * Where "new" begins for this viewing.
   *
   * Captured when the panel opens, because marking read moves the server's
   * read point within seconds -- judged against that, a message would lose its
   * NEW marker almost as soon as it appeared, before anyone had read it.
   */
  const [newSince, setNewSince] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (!open) setNewSince(undefined);
    else if (data && newSince === undefined) setNewSince(data.last_read_at);
  }, [open, data, newSince]);

  // Mark read while open, up to the newest message actually rendered. The
  // sent ref stops an unchanged conversation re-posting on every poll.
  const sent = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !data || document.visibilityState !== "visible") return;
    if (!needsMarking(data.messages, data.last_read_at)) return;
    const through = readThrough(data.messages);
    if (!through || sent.current === through) return;
    sent.current = through;
    api.markRead(deviceId, through).then(notifyUnreadChanged, () => {
      // Leave it unmarked and let the next poll try again, rather than
      // pretending it worked.
      sent.current = null;
    });
  }, [open, data, deviceId]);

  // Scroll only when the conversation actually grew. Doing it on every poll
  // would yank the view back down while someone is reading older messages.
  useEffect(() => {
    if (open && count > previousCount.current) {
      endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    previousCount.current = count;
  }, [count, open]);

  const unread = data ? unreadCount(data.messages, data.last_read_at) : 0;

  // Previews are object URLs; released when they leave, or the tab holds
  // every photo anyone ever attached until it is closed.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(() => () => attachmentsRef.current.forEach((a) => URL.revokeObjectURL(a.preview)), []);

  const attach = async (files: readonly File[]) => {
    const images = files.filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return;
    const room = MAX_PHOTOS - attachments.length - preparing;
    if (images.length > room) {
      setActionError(`At most ${MAX_PHOTOS} photos per message`);
      if (room <= 0) return;
    } else {
      setActionError(null);
    }
    const taken = images.slice(0, Math.max(0, room));
    setPreparing((n) => n + taken.length);
    for (const file of taken) {
      try {
        const prepared = await preparePhoto(file);
        setAttachments((list) => [...list, prepared]);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Could not read that image");
      } finally {
        setPreparing((n) => n - 1);
      }
    }
  };

  const unattach = (index: number) => {
    setAttachments((list) => {
      URL.revokeObjectURL(list[index]!.preview);
      return list.filter((_, i) => i !== index);
    });
  };

  const canSend = (draft.trim() !== "" || attachments.length > 0) && preparing === 0 && !busy;

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (!canSend) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.post(
        deviceId,
        body,
        attachments.map(({ data_base64, thumb_base64 }) => ({ data_base64, thumb_base64 })),
      );
      setDraft("");
      attachments.forEach((a) => URL.revokeObjectURL(a.preview));
      setAttachments([]);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not send");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (message: DeviceMessage) => {
    if (!confirm("Delete this message?")) return;
    setActionError(null);
    try {
      await api.remove(deviceId, message.id);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not delete");
    }
  };

  // ------------------------------------------------------------ collapsed --
  if (!open && mode === "column") {
    return (
      <button
        onClick={onToggle}
        title="Show the discussion"
        aria-expanded={false}
        style={{
          ...collapsedTab,
          borderColor: unread > 0 ? "var(--status-warning)" : "var(--border)",
          color: unread > 0 ? "var(--status-warning)" : "var(--text-secondary)",
        }}
      >
        <span style={{ writingMode: "vertical-rl", letterSpacing: "0.2em" }}>DISCUSSION</span>
        {unread > 0 && <span style={{ marginTop: 10, fontWeight: 600 }}>{unread}</span>}
      </button>
    );
  }

  const header = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
      <h2 style={sectionTitle}>
        &gt; DISCUSSION{count > 0 ? ` (${count})` : ""}
        {unread > 0 && !open && (
          <span style={{ color: "var(--status-warning)", fontSize: 11, marginLeft: 8 }}>{unread} NEW</span>
        )}
      </h2>
      <button onClick={onToggle} aria-expanded={open} title={open ? "Hide the discussion" : "Show the discussion"} style={smallButton}>
        {mode === "column" ? "[ » ]" : open ? "[ HIDE ]" : "[ SHOW ]"}
      </button>
    </div>
  );

  if (!open) {
    // Stacked and collapsed: just the header, carrying the unread count.
    return <section style={{ ...card, padding: "12px 16px" }}>{header}</section>;
  }

  return (
    <section
      style={
        mode === "column"
          ? {
              ...card,
              marginBottom: 0,
              // Pinned beside the charts as the page scrolls, with its own
              // scroll for the conversation and the box always in reach.
              position: "sticky",
              top: 12,
              height: "calc(100vh - 110px)",
              minHeight: 360,
              display: "flex",
              flexDirection: "column",
              padding: 16,
            }
          : card
      }
    >
      {header}
      <p style={help}>{audienceNote(data)}</p>

      {actionError && <p style={{ color: "var(--status-critical)", fontSize: 12 }}>{`!! ${actionError}`}</p>}
      {error && !data && <p style={{ color: "var(--status-critical)", fontSize: 12 }}>{`!! ${error}`}</p>}
      {data?.truncated && (
        <p style={{ ...help, color: "var(--status-warning)" }}>Older messages are hidden.</p>
      )}

      <div style={mode === "column" ? { flex: 1, overflowY: "auto", minHeight: 0 } : { maxHeight: 340, overflowY: "auto" }}>
        {data && data.messages.length === 0 && (
          <p style={{ color: "var(--text-muted)", fontSize: 13 }}>-- NOTHING YET --</p>
        )}
        {data?.messages.map((message) => {
          const isNew =
            !message.mine &&
            newSince !== undefined &&
            (newSince === null || Date.parse(message.created_at) > Date.parse(newSince));
          return (
            <div
              key={message.id}
              style={{
                padding: "8px 0 8px 8px",
                borderBottom: "1px solid var(--gridline)",
                // A bar rather than a colour wash: it marks the message
                // without changing how its text reads.
                borderLeft: `2px solid ${isNew ? "var(--status-warning)" : "transparent"}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span style={{ color: message.mine ? "var(--text-primary)" : "var(--text-secondary)", fontSize: 12 }}>
                  {message.author_label}
                </span>
                <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{message.author_org_label}</span>
                <span style={{ color: "var(--text-muted)", fontSize: 11 }}>{relativeTime(message.created_at, now)}</span>
                {isNew && <span style={{ color: "var(--status-warning)", fontSize: 10, letterSpacing: "0.1em" }}>NEW</span>}
                {message.can_delete && (
                  <button
                    onClick={() => void remove(message)}
                    style={{ ...smallButton, padding: "0 5px", color: "var(--status-critical)" }}
                    title="Delete this message"
                  >
                    x
                  </button>
                )}
              </div>
              {/* pre-wrap, because people paste log lines in here and the line
                  breaks are the reason they were worth pasting. */}
              {message.body !== "" && (
                <div style={{ fontSize: 13, whiteSpace: "pre-wrap", marginTop: 3, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                  {message.body}
                </div>
              )}
              {message.photos.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {message.photos.map((photo) => (
                    <a
                      key={photo.id}
                      href={api.photoUrl(deviceId, message.id, photo.id, "full")}
                      target="_blank"
                      rel="noreferrer"
                      title={`Open full size (${photo.width}×${photo.height})`}
                    >
                      {/* aspect-ratio reserves the space before the image
                          arrives, so the conversation does not jump -- or
                          scroll away from the newest message -- as
                          thumbnails load in. */}
                      <img
                        src={api.photoUrl(deviceId, message.id, photo.id, "thumb")}
                        alt="Photo"
                        loading="lazy"
                        style={{
                          display: "block",
                          width: message.photos.length === 1 ? 220 : 120,
                          maxWidth: "100%",
                          aspectRatio: `${photo.width} / ${photo.height}`,
                          objectFit: "cover",
                          border: "1px solid var(--border)",
                          background: "var(--page-plane)",
                        }}
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {(attachments.length > 0 || preparing > 0) && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
          {attachments.map((a, i) => (
            <div key={a.preview} style={{ position: "relative" }}>
              <img src={a.preview} alt="To send" style={{ display: "block", height: 56, border: "1px solid var(--border)" }} />
              <button
                type="button"
                onClick={() => unattach(i)}
                title="Remove this photo"
                style={{ ...smallButton, position: "absolute", top: 2, right: 2, padding: "0 4px", background: "var(--surface-1)", color: "var(--status-critical)" }}
              >
                x
              </button>
            </div>
          ))}
          {preparing > 0 && <span style={{ ...help, margin: 0, alignSelf: "center" }}>{">> preparing…"}</span>}
        </div>
      )}

      <form onSubmit={(e) => void send(e)} style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 12 }}>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            void attach([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
        />
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; a new line takes a modifier. The opposite would
            // make the common case two keystrokes to spare the rare one.
            const action = enterAction({
              key: e.key, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey, isComposing: e.nativeEvent.isComposing,
            });
            if (action === "send") {
              e.preventDefault();
              void send(e);
            } else if (action === "newline") {
              e.preventDefault();
              const box = e.currentTarget;
              box.setRangeText("\n", box.selectionStart, box.selectionEnd, "end");
              setDraft(box.value);
            }
          }}
          onPaste={(e) => {
            // A screenshot pasted from the clipboard arrives as a file. Text
            // pastes are left to the browser.
            const files = [...e.clipboardData.files].filter((f) => f.type.startsWith("image/"));
            if (files.length > 0) {
              e.preventDefault();
              void attach(files);
            }
          }}
          rows={2}
          maxLength={4000}
          placeholder="Message (Ctrl+Enter for a new line)"
          style={{
            flex: 1,
            minWidth: 0,
            padding: "7px 9px",
            fontSize: 13,
            fontFamily: "inherit",
            border: "1px solid var(--border)",
            background: "var(--page-plane)",
            color: "var(--text-primary)",
            resize: "vertical",
          }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={attachments.length + preparing >= MAX_PHOTOS}
            title={`Attach up to ${MAX_PHOTOS} photos`}
            style={{ ...smallButton, padding: "4px 12px" }}
          >
            [ PHOTO ]
          </button>
          <button type="submit" disabled={!canSend} style={{ ...smallButton, padding: "4px 12px" }}>
            [ SEND ]
          </button>
        </div>
      </form>
    </section>
  );
}

/**
 * Who can read what you are about to write.
 *
 * Worded from what the reader is allowed to know. The owner is told the exact
 * number of organizations; an organization the device is shared with is told
 * that others may be here, because it cannot see who a device reaches and a
 * count would be a new way to find out.
 */
function audienceNote(data: { is_owner: boolean; shared_with_count: number | null } | null): string {
  if (!data) return "Visible to everyone who can see this device.";
  if (!data.is_owner) {
    return "Owned by another organization. They, and anyone they share it with, see your messages.";
  }
  const count = data.shared_with_count ?? 0;
  if (count === 0) {
    return "Visible to your organization, and to anyone you share this device with.";
  }
  return `Visible to your organization and the ${count} organization${count === 1 ? "" : "s"} it's shared with.`;
}

const card: React.CSSProperties = {
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  padding: 20,
  marginBottom: 16,
};
const collapsedTab: React.CSSProperties = {
  position: "sticky",
  top: 12,
  width: 34,
  minHeight: 160,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "12px 0",
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  fontSize: 11,
  fontFamily: "inherit",
  cursor: "pointer",
};
const sectionTitle: React.CSSProperties = { fontSize: 14, margin: 0, letterSpacing: "0.12em" };
const help: React.CSSProperties = {
  color: "var(--text-muted)", fontSize: 11, margin: "6px 0 12px", lineHeight: 1.5,
};
const smallButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 9px",
  fontSize: 11, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em",
};
